'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  FolderOpen,
  Loader2,
  Plus,
  Search,
  Shield,
  Target,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { Progress } from '@/components/ui/progress';
import { MobilePanelSectionLabel } from '@/components/ui/design-system';
import { UserAuthForm } from '@/components/features/auth/SignInForm';
import { waitForAuthenticatedSession } from '@/utils/auth/client-session';
import { ResumeListItem, type ResumeListItemData } from './ResumeListItem';
import { SimulatedUploadProgress } from './SimulatedUploadProgress';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  ACCEPTED_RESUME_FILE_EXTENSIONS,
  RESUME_FORMATS_HUMAN_LABEL,
  RESUME_MAX_SIZE_HUMAN_LABEL,
  validateResumeFile,
} from '@/lib/resume/upload-constants';

const PAGE_SIZE = 4;

type ResumeSourceType = 'uploaded' | 'builder';

export interface IntakeResume extends ResumeListItemData {
  sourceType: ResumeSourceType;
  viewUrl: string;
  linkedBuilderId: string | null;
}

type ActiveMode = 'idle' | 'create' | 'upload';
type OperationKind = 'create' | 'upload' | 'select';

interface UploadedResumeApiItem {
  id: string;
  title?: string;
  fileName?: string;
  filename?: string;
  fileSize?: number;
  fileMime?: string;
  createdAt?: string;
  updatedAt?: string;
  uploadedAt?: string;
  url?: string;
  linkedBuilderReportId?: string | null;
}

interface BuilderResumeApiItem {
  id: string;
  name?: string;
  version?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

function toIsoOrNow(value?: string): string {
  const parsed = value ? new Date(value) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

function mapUploadedResume(item: UploadedResumeApiItem): IntakeResume {
  const fileName = item.fileName || item.filename || item.title || 'Uploaded resume';
  return {
    id: item.id,
    title: item.title || fileName,
    fileName,
    fileSize: item.fileSize || 0,
    fileMime: item.fileMime || 'application/octet-stream',
    createdAt: toIsoOrNow(item.createdAt || item.uploadedAt),
    updatedAt: toIsoOrNow(item.updatedAt || item.createdAt || item.uploadedAt),
    sourceType: 'uploaded',
    viewUrl: item.url || '/d/pitch/resumes',
    linkedBuilderId: item.linkedBuilderReportId ?? null,
  };
}

function mapBuilderResume(item: BuilderResumeApiItem): IntakeResume {
  const versionLabel = item.version ? `v${item.version}` : 'Builder';
  const statusLabel = item.status ? ` • ${item.status}` : '';
  return {
    id: item.id,
    title: item.name || 'Untitled Resume',
    fileName: `Resume Builder ${versionLabel}${statusLabel}`,
    fileSize: 0,
    fileMime: 'application/json',
    createdAt: toIsoOrNow(item.createdAt),
    updatedAt: toIsoOrNow(item.updatedAt || item.createdAt),
    sourceType: 'builder',
    viewUrl: `/d/pitch/resumes/builder/${item.id}`,
    linkedBuilderId: item.id,
  };
}

function ActionTile({
  icon: Icon,
  label,
  sublabel,
  active,
  disabled,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  sublabel: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 border px-3.5 py-3 text-left transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-foreground hover:border-primary/60',
        disabled && 'opacity-50 cursor-not-allowed hover:border-border',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center border',
          active
            ? 'border-primary-foreground/40 bg-primary-foreground/10'
            : 'border-border bg-card',
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold tracking-tight">{label}</p>
        <p className={cn('text-xs', active ? 'text-primary-foreground/75' : 'text-muted-foreground')}>
          {sublabel}
        </p>
      </div>
    </button>
  );
}

// ─── Progress reporter ───────────────────────────────────────────────────────

export interface IntakeStage {
  /** Stable identifier referenced by setStage(). */
  key: string;
  /** Human-readable label shown in the progress strip. */
  label: string;
}

export interface IntakeProgressReporter {
  /** Declare the ordered stages this operation will go through. */
  setStages: (stages: IntakeStage[]) => void;
  /** Mark a previously-declared stage as currently active. */
  setStage: (key: string) => void;
}

interface ProgressState {
  kind: OperationKind;
  stages: IntakeStage[];
  currentKey: string | null;
}

const OPERATION_HEADINGS: Record<OperationKind, string> = {
  create: 'Creating resume',
  upload: 'Uploading resume',
  select: 'Opening resume',
};

const DEFAULT_STAGE: Record<OperationKind, IntakeStage> = {
  create: { key: 'create', label: 'Creating resume…' },
  upload: { key: 'upload', label: 'Uploading file…' },
  select: { key: 'open', label: 'Opening resume…' },
};

// ─── Result + props ──────────────────────────────────────────────────────────

export interface IntakeHandlerResult {
  resumeId?: string;
  /** Default true on success — the panel calls onClose after resetting. */
  shouldClose?: boolean;
}

export interface ResumeIntakePanelProps {
  /**
   * Called when the user clicks "Create". The reporter lets the host declare
   * its real stages so the progress strip stays honest.
   */
  onCreate?: (
    name: string,
    progress: IntakeProgressReporter,
  ) => Promise<IntakeHandlerResult | void>;
  /** Called when the user submits a chosen file. */
  onUpload?: (
    file: File,
    progress: IntakeProgressReporter,
  ) => Promise<IntakeHandlerResult | void>;
  /** Called when the user clicks an existing resume row. */
  onSelectExisting?: (
    resume: IntakeResume,
    progress: IntakeProgressReporter,
  ) => Promise<IntakeHandlerResult | void>;
  /**
   * When provided, a row click opens the resume for preview *in place* via this
   * callback instead of running the `onSelectExisting`/navigate flow — so no
   * "Opening resume…" progress strip and no page navigation. Used by onboarding
   * to show the resume in a dialog without leaving the flow. When omitted, the
   * panel keeps its default navigate-on-select behavior for all other callers.
   */
  onPreviewExisting?: (resume: IntakeResume) => void;
  /** Called by the panel after a successful operation to let the host close its dialog. */
  onClose?: () => void;
  createSublabel?: string;
  uploadSublabel?: string;
  createSubmitLabel?: string;
  hideExistingList?: boolean;
  /**
   * When true, the "Your Resumes" list is collapsed behind a "View resumes"
   * tile (only shown once the user actually has resumes) instead of always
   * rendering the full, paginated list. Keeps the section compact when a
   * resume already exists.
   */
  collapsibleExistingList?: boolean;
  /**
   * When true, hides "Create New" and "Your Resumes" sections — only shows
   * "Upload Resume" with immediate file upload. Used for unauthenticated users
   * who can still upload for text extraction without saving to their account.
   */
  anonymousMode?: boolean;
  initialMode?: ActiveMode;
  className?: string;
  /**
   * Post-load filter applied to the merged resume list. Use to exclude
   * certain resumes (e.g. tailored copies) from appearing in the panel.
   */
  filterResumes?: (resumes: IntakeResume[]) => IntakeResume[];
  /**
   * When set, the resume row with this ID renders in its selected/highlighted
   * state. The panel does not manage selection itself — the host controls it
   * via `onPreviewExisting` and this prop.
   */
  selectedResumeId?: string | null;
  /**
   * Called once after resumes finish loading (and after `filterResumes` is
   * applied). Lets the host inspect the list — e.g. to auto-select the
   * user's active resume.
   */
  onResumesLoaded?: (resumes: IntakeResume[]) => void;
}

export function ResumeIntakePanel({
  onCreate,
  onUpload,
  onSelectExisting,
  onPreviewExisting,
  onClose,
  createSublabel = 'Start from a blank resume',
  uploadSublabel = `${RESUME_FORMATS_HUMAN_LABEL} · ${RESUME_MAX_SIZE_HUMAN_LABEL}`,
  createSubmitLabel = 'Create Resume',
  hideExistingList = false,
  collapsibleExistingList = false,
  anonymousMode = false,
  initialMode = 'idle',
  className,
  filterResumes,
  selectedResumeId,
  onResumesLoaded,
}: ResumeIntakePanelProps) {
  const { status: authStatus, update: updateSession } = useSession();
  const isAuthenticated = authStatus === 'authenticated';

  // In anonymous mode, force-hide create and existing list, default to upload mode
  const effectiveHideExistingList = hideExistingList || anonymousMode;
  const effectiveInitialMode: ActiveMode = anonymousMode ? 'upload' : initialMode;
  const showCreateTile = !anonymousMode;
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeMode, setActiveMode] = useState<ActiveMode>(effectiveInitialMode);
  const [name, setName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Auth-gate state for anonymous mode: file is held until sign-in completes
  const [pendingAuthFile, setPendingAuthFile] = useState<File | null>(null);
  const [isAuthSyncing, setIsAuthSyncing] = useState(false);
  const [authSyncProgress, setAuthSyncProgress] = useState(0);
  // Direct sign-in flow (without pending file) for users who already have an account
  const [showDirectSignIn, setShowDirectSignIn] = useState(false);

  const [resumes, setResumes] = useState<IntakeResume[]>([]);
  const [isLoadingResumes, setIsLoadingResumes] = useState(true);
  // When collapsible, the list starts hidden behind the "View resumes" tile.
  const [listOpen, setListOpen] = useState(!collapsibleExistingList);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectingResumeId, setSelectingResumeId] = useState<string | null>(null);
  // One-shot guard for the "0 resumes → open Upload by default" behavior below.
  // Flipped true once we've resolved the initial mode (auto-opened, saw existing
  // resumes, or the user interacted) so it never springs open again mid-session.
  const initialModeSettledRef = useRef(false);

  const [progress, setProgress] = useState<ProgressState | null>(null);
  const isWorking = progress !== null;

  // Listen for auth-success postMessage (from sign-in new tab), then upload
  useEffect(() => {
    if (!pendingAuthFile) return;

    const handleAuthSuccess = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data !== 'auth-success') return;

      void (async () => {
        setIsAuthSyncing(true);
        setAuthSyncProgress(10);

        const synced = await waitForAuthenticatedSession(
          typeof updateSession === 'function' ? updateSession : undefined,
          {
            maxAttempts: 12,
            intervalMs: 250,
            onAttempt: (attempt, maxAttempts) => {
              setAuthSyncProgress(Math.min(96, Math.round(10 + (attempt / maxAttempts) * 80)));
            },
          },
        );

        setAuthSyncProgress(100);
        setIsAuthSyncing(false);

        if (!synced) {
          toast({
            variant: 'destructive',
            title: 'Sign-in failed',
            description: "We couldn't verify your session. Please try again.",
          });
          return;
        }

        const file = pendingAuthFile;
        setPendingAuthFile(null);

        try {
          const result = await runWithProgress('upload', (reporter) =>
            (onUpload ?? defaultUpload)(file, reporter),
          );
          toast({ title: 'Resume uploaded successfully.' });
          if (result?.shouldClose !== false) {
            resetForms();
            onClose?.();
          }
        } catch (err) {
          toast({
            variant: 'destructive',
            title: 'Upload failed',
            description: err instanceof Error ? err.message : 'Failed to upload resume',
          });
        }
      })();
    };

    window.addEventListener('message', handleAuthSuccess);
    return () => window.removeEventListener('message', handleAuthSuccess);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAuthFile]);

  // Listen for auth-success when user signs in directly (without pending file)
  useEffect(() => {
    if (!showDirectSignIn) return;

    const handleAuthSuccess = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data !== 'auth-success') return;

      void (async () => {
        setIsAuthSyncing(true);
        setAuthSyncProgress(10);

        const synced = await waitForAuthenticatedSession(
          typeof updateSession === 'function' ? updateSession : undefined,
          {
            maxAttempts: 12,
            intervalMs: 250,
            onAttempt: (attempt, maxAttempts) => {
              setAuthSyncProgress(Math.min(96, Math.round(10 + (attempt / maxAttempts) * 80)));
            },
          },
        );

        setAuthSyncProgress(100);
        setIsAuthSyncing(false);

        if (synced) {
          setShowDirectSignIn(false);
        } else {
          toast({
            variant: 'destructive',
            title: 'Sign-in failed',
            description: "We couldn't verify your session. Please try again.",
          });
        }
      })();
    };

    window.addEventListener('message', handleAuthSuccess);
    return () => window.removeEventListener('message', handleAuthSuccess);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDirectSignIn, updateSession]);

  const loadResumes = useCallback(async () => {
    if (effectiveHideExistingList) {
      setIsLoadingResumes(false);
      return;
    }
    setIsLoadingResumes(true);
    try {
      const [uploadedRes, builderRes] = await Promise.all([
        fetch('/api/settings/resumes', { cache: 'no-store' }),
        fetch('/api/resume-builder', { cache: 'no-store' }),
      ]);
      const uploadedPayload = (await uploadedRes.json().catch(() => null)) as
        | { resumes?: UploadedResumeApiItem[] }
        | null;
      const builderPayload = (await builderRes.json().catch(() => null)) as
        | { resumes?: BuilderResumeApiItem[] }
        | null;

      const builderIds = new Set((builderPayload?.resumes ?? []).map((r) => r.id));
      const uploaded = (uploadedPayload?.resumes ?? [])
        .filter((r) => !r.linkedBuilderReportId || !builderIds.has(r.linkedBuilderReportId))
        .map(mapUploadedResume);
      const builder = (builderPayload?.resumes ?? []).map(mapBuilderResume);

      let merged = [...uploaded, ...builder].sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt).getTime() -
          new Date(a.updatedAt || a.createdAt).getTime(),
      );
      if (filterResumes) merged = filterResumes(merged);
      setResumes(merged);
      setCurrentPage(0);
      onResumesLoaded?.(merged);
    } catch (err) {
      console.error('Failed to load resumes:', err);
    } finally {
      setIsLoadingResumes(false);
    }
  }, [effectiveHideExistingList, filterResumes, onResumesLoaded]);

  useEffect(() => {
    void loadResumes();
  }, [loadResumes]);

  // When the user has no resumes yet, open the Upload tile by default so the drop
  // zone is immediately visible — the empty state's whole purpose is to add one.
  // Baked into the panel so every caller (onboarding, dialogs, etc.) behaves the
  // same. Only fires once, and never when the caller set an explicit initial mode
  // (e.g. anonymous upload), when the list is hidden (count unknown), or after the
  // user has already picked a tile — see `initialModeSettledRef`.
  useEffect(() => {
    if (initialModeSettledRef.current) return;
    if (effectiveInitialMode !== 'idle') return;
    if (effectiveHideExistingList) return;
    if (isLoadingResumes) return;
    initialModeSettledRef.current = true;
    if (resumes.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveMode('upload');
    }
  }, [isLoadingResumes, resumes.length, effectiveHideExistingList, effectiveInitialMode]);

  const totalPages = Math.max(1, Math.ceil(resumes.length / PAGE_SIZE));
  const clampedPage = Math.min(currentPage, totalPages - 1);
  const visibleResumes = useMemo(
    () => resumes.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE),
    [resumes, clampedPage],
  );

  const resetForms = useCallback(() => {
    setName('');
    setSelectedFile(null);
    setDragActive(false);
    setActiveMode('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /**
   * Run an operation with progress tracking. The reporter passed to the host
   * lets it declare real stages; if the host never calls setStages we fall
   * back to a single generic stage so the strip still shows something honest.
   */
  const runWithProgress = useCallback(
    async (
      kind: OperationKind,
      run: (reporter: IntakeProgressReporter) => Promise<IntakeHandlerResult | void>,
    ): Promise<IntakeHandlerResult | undefined> => {
      const fallbackStage = DEFAULT_STAGE[kind];
      setProgress({ kind, stages: [fallbackStage], currentKey: fallbackStage.key });

      const reporter: IntakeProgressReporter = {
        setStages: (stages) => {
          if (stages.length === 0) return;
          setProgress({ kind, stages, currentKey: stages[0].key });
        },
        setStage: (key) => {
          setProgress((prev) =>
            prev && prev.stages.some((s) => s.key === key)
              ? { ...prev, currentKey: key }
              : prev,
          );
        },
      };

      try {
        const result = (await run(reporter)) ?? {};
        return result;
      } finally {
        setProgress(null);
      }
    },
    [],
  );

  // The three tiles (Create / Upload / View resumes) act as a single radio
  // group: opening one collapses the others. `activeMode` and `listOpen` are
  // separate states, so we clear the list whenever a mode tile is toggled on.
  const toggleMode = (next: ActiveMode) => {
    initialModeSettledRef.current = true;
    setActiveMode((prev) => (prev === next ? 'idle' : next));
    if (collapsibleExistingList) setListOpen(false);
  };

  const toggleList = () => {
    initialModeSettledRef.current = true;
    setListOpen((open) => !open);
    setActiveMode('idle');
  };

  const defaultCreate = useCallback(
    async (
      resumeName: string,
      reporter: IntakeProgressReporter,
    ): Promise<IntakeHandlerResult> => {
      reporter.setStages([
        { key: 'create', label: 'Creating resume…' },
        { key: 'open', label: 'Opening builder…' },
      ]);
      const response = await fetch('/api/resume-builder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: resumeName }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { resume?: { id?: string }; error?: string }
        | null;
      const resumeId = payload?.resume?.id;
      if (!response.ok || !resumeId) {
        throw new Error(payload?.error || 'Failed to create resume');
      }
      reporter.setStage('open');
      window.location.href = `/d/pitch/resumes/builder/${resumeId}`;
      return { resumeId, shouldClose: true };
    },
    [],
  );

  const defaultUpload = useCallback(
    async (
      file: File,
      reporter: IntakeProgressReporter,
    ): Promise<IntakeHandlerResult> => {
      reporter.setStages([{ key: 'upload', label: 'Uploading and parsing…' }]);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      const response = await fetch('/api/settings/resumes', {
        method: 'POST',
        body: formData,
      });
      const payload = (await response.json().catch(() => null)) as
        | { success?: boolean; resume?: { id?: string }; error?: string }
        | null;
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to upload resume');
      }
      return { resumeId: payload.resume?.id, shouldClose: true };
    },
    [],
  );

  const defaultSelectExisting = useCallback(
    async (
      resume: IntakeResume,
      reporter: IntakeProgressReporter,
    ): Promise<IntakeHandlerResult> => {
      reporter.setStages([{ key: 'open', label: 'Opening resume…' }]);
      if (resume.viewUrl) {
        window.location.href = resume.viewUrl;
      }
      return { resumeId: resume.id, shouldClose: true };
    },
    [],
  );

  const handleCreate = async () => {
    const finalName = name.trim();
    if (!finalName || isWorking) return;
    try {
      const result = await runWithProgress('create', (reporter) =>
        (onCreate ?? defaultCreate)(finalName, reporter),
      );
      if (result?.shouldClose !== false) {
        resetForms();
        onClose?.();
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to create resume',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const handleFileChosen = (file: File) => {
    const validation = validateResumeFile(file);
    if (!validation.ok) {
      toast({
        variant: 'destructive',
        title: validation.reason === 'too-large' ? 'File too large' : 'Invalid file type',
        description: validation.message,
      });
      return;
    }
    setSelectedFile(file);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || isWorking) return;

    // In anonymous mode, gate on auth: hold the file and show sign-in
    if (anonymousMode && !isAuthenticated) {
      setPendingAuthFile(selectedFile);
      return;
    }

    try {
      const result = await runWithProgress('upload', (reporter) =>
        (onUpload ?? defaultUpload)(selectedFile, reporter),
      );
      toast({ title: 'Resume uploaded successfully.' });
      if (result?.shouldClose !== false) {
        resetForms();
        onClose?.();
      } else {
        void loadResumes();
        resetForms();
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload resume',
      });
    }
  };

  const handleExistingClick = async (resume: IntakeResume) => {
    if (isWorking) return;
    // Preview-in-place mode (onboarding): open the dialog instantly — no
    // progress strip, no navigation, no per-row spinner.
    if (onPreviewExisting) {
      onPreviewExisting(resume);
      return;
    }
    setSelectingResumeId(resume.id);
    try {
      const result = await runWithProgress('select', (reporter) =>
        (onSelectExisting ?? defaultSelectExisting)(resume, reporter),
      );
      if (result?.shouldClose !== false) {
        onClose?.();
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to open resume',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSelectingResumeId(null);
    }
  };

  const progressPercent = useMemo(() => {
    if (!progress) return 0;
    const idx = progress.stages.findIndex((s) => s.key === progress.currentKey);
    if (idx < 0) return 0;
    return Math.round(((idx + 1) / progress.stages.length) * 100);
  }, [progress]);

  return (
    <div className={cn('space-y-3', className)}>
      {progress && progress.kind === 'upload' && (
        // Uploading + parsing takes several seconds with no real progress signal,
        // so narrate it with a simulated multi-step animation instead of a single
        // flat bar. Create/select keep the honest reporter-driven strip below.
        <SimulatedUploadProgress fileName={selectedFile?.name ?? pendingAuthFile?.name ?? undefined} />
      )}

      {progress && progress.kind !== 'upload' && (
        <div className="space-y-3 border border-primary/40 bg-primary/[0.04] p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-foreground">
              {OPERATION_HEADINGS[progress.kind]}
            </p>
            <p className="text-xs text-muted-foreground tabular-nums">{progressPercent}%</p>
          </div>
          <Progress value={progressPercent} />
          <div className="space-y-1.5">
            {progress.stages.map((stage) => {
              const currentIndex = progress.stages.findIndex(
                (s) => s.key === progress.currentKey,
              );
              const stageIndex = progress.stages.findIndex((s) => s.key === stage.key);
              const state =
                stageIndex < currentIndex
                  ? 'done'
                  : stageIndex === currentIndex
                    ? 'active'
                    : 'pending';
              return (
                <div key={stage.key} className="flex items-center gap-2 text-xs">
                  {state === 'done' ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  ) : state === 'active' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
                  ) : (
                    <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-border" />
                  )}
                  <span
                    className={cn(
                      state === 'active' && 'text-foreground font-medium',
                      state === 'done' && 'text-muted-foreground line-through',
                      state === 'pending' && 'text-muted-foreground',
                    )}
                  >
                    {stage.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {anonymousMode && !pendingAuthFile && (
        <div className="border border-border/60 bg-muted/15 p-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
            Why add your resume?
          </p>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Target className="h-3.5 w-3.5 mt-0.5 text-primary/60 shrink-0" />
            <span>Your resume helps us match you to the right roles and companies</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Search className="h-3.5 w-3.5 mt-0.5 text-primary/60 shrink-0" />
            <span>We extract skills, experience, and preferences to personalize results</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5 mt-0.5 text-primary/60 shrink-0" />
            <span>Your data stays private and is only used to improve your search</span>
          </div>
        </div>
      )}

      {!pendingAuthFile && (
        <div className="space-y-1.5">
          {showCreateTile && (
            <ActionTile
              icon={Plus}
              label="Create New"
              sublabel={createSublabel}
              active={activeMode === 'create'}
              disabled={isWorking && activeMode !== 'create'}
              onClick={() => toggleMode('create')}
            />
          )}
          <ActionTile
            icon={Upload}
            label="Upload Resume"
            sublabel={uploadSublabel}
            active={activeMode === 'upload'}
            disabled={isWorking && activeMode !== 'upload'}
            onClick={() => toggleMode('upload')}
          />
          {collapsibleExistingList && !effectiveHideExistingList && !isLoadingResumes && resumes.length > 0 && (
            <ActionTile
              icon={FolderOpen}
              label={listOpen ? 'Hide resumes' : 'View resumes'}
              sublabel={
                resumes.length === 1
                  ? 'You already have 1 resume'
                  : `You already have ${resumes.length} resumes`
              }
              active={listOpen}
              disabled={isWorking}
              onClick={toggleList}
            />
          )}
        </div>
      )}

      {!pendingAuthFile && activeMode === 'create' && (
        <div className="space-y-2 border border-border bg-background p-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Senior Product Engineer Resume"
            autoFocus
            disabled={isWorking}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
          />
          <Button
            onClick={() => void handleCreate()}
            disabled={!name.trim() || isWorking}
            className="w-full"
          >
            {isWorking && progress?.kind === 'create' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              createSubmitLabel
            )}
          </Button>
        </div>
      )}

      {!pendingAuthFile && activeMode === 'upload' && (
        <div className="space-y-2 border border-border bg-background p-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_RESUME_FILE_EXTENSIONS}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileChosen(file);
            }}
          />
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-2 border border-dashed py-6 text-center cursor-pointer transition-colors',
              isWorking && 'pointer-events-none opacity-60',
              dragActive
                ? 'border-primary bg-primary/10'
                : selectedFile
                  ? 'border-green-500 bg-green-50 dark:bg-green-950'
                  : 'border-border hover:border-primary',
            )}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const file = e.dataTransfer.files?.[0];
              if (file) handleFileChosen(file);
            }}
            onClick={() => !isWorking && fileInputRef.current?.click()}
          >
            {selectedFile ? (
              <>
                <FileText className="h-7 w-7 text-green-600" />
                <div>
                  <p className="font-bold text-foreground text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {(selectedFile.size / 1024).toFixed(0)} KB · Click to change
                  </p>
                </div>
              </>
            ) : (
              <>
                <Upload className="h-7 w-7 text-muted-foreground" />
                <div>
                  <p className="font-bold text-foreground text-sm">Drop your resume here</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    or click to browse · {RESUME_FORMATS_HUMAN_LABEL}
                  </p>
                </div>
              </>
            )}
          </div>
          <Button
            onClick={() => void handleUploadSubmit()}
            disabled={!selectedFile || isWorking}
            className="w-full"
          >
            {isWorking && progress?.kind === 'upload' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              'Upload Resume'
            )}
          </Button>
        </div>
      )}

      {anonymousMode && !pendingAuthFile && (
        <div className="border-t border-border/40 pt-3">
          <p className="text-xs text-muted-foreground">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => setShowDirectSignIn(true)}
              className="text-primary font-medium hover:underline"
            >
              Sign in
            </button>
            {' '}to access your saved resumes.
          </p>
          {showDirectSignIn && (
            <div className="mt-3">
              {isAuthSyncing ? (
                <div className="space-y-2 border border-primary/20 bg-primary/5 px-3 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    Finalizing sign in…
                  </p>
                  <Progress value={authSyncProgress} className="h-2" />
                </div>
              ) : (
                <UserAuthForm
                  mode="sign-in"
                  className="w-full gap-4 [&_form]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:bg-background [&_button]:w-full [&_button]:h-10 [&_.auth-divider-label]:bg-primary/[0.04] [&_.auth-divider-label]:px-3"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Auth gate step: shown when anonymous user has selected a file */}
      {pendingAuthFile && !isAuthenticated && (
        <div className="space-y-3 border border-primary/40 bg-primary/[0.04] p-4">
          <div className="flex items-center gap-3 border border-border/60 bg-muted/20 px-3 py-2.5">
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-wide text-primary">Selected resume</p>
              <p className="text-sm font-semibold text-foreground truncate mt-0.5">{pendingAuthFile.name}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setPendingAuthFile(null);
                setIsAuthSyncing(false);
                setAuthSyncProgress(0);
              }}
            >
              Change
            </Button>
          </div>

          <div>
            <p className="text-sm font-bold text-foreground">Sign in to continue</p>
            <p className="text-xs text-muted-foreground mt-1">
              Sign in to attach this resume to your account and enrich your search.
            </p>
          </div>

          {isAuthSyncing ? (
            <div className="space-y-2 border border-primary/20 bg-primary/5 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                Finalizing sign in…
              </p>
              <Progress value={authSyncProgress} className="h-2" />
            </div>
          ) : (
            <UserAuthForm
              mode="sign-in"
              className="w-full gap-4 [&_form]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:bg-background [&_button]:w-full [&_button]:h-10 [&_.auth-divider-label]:bg-primary/[0.04] [&_.auth-divider-label]:px-3"
            />
          )}
        </div>
      )}

      {!effectiveHideExistingList && (!collapsibleExistingList || listOpen) && (
        <div>
          <MobilePanelSectionLabel>Your Resumes</MobilePanelSectionLabel>
          <div className="mt-1.5">
            {isLoadingResumes ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : resumes.length === 0 ? (
              <div className="border border-dashed border-border py-8 text-center">
                <FileText className="mx-auto h-7 w-7 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  No resumes yet. Create or upload one above.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {visibleResumes.map((resume) => (
                    <ResumeListItem
                      key={resume.id}
                      resume={resume}
                      isSelected={selectedResumeId != null && resume.id === selectedResumeId}
                      showRefineButton={false}
                      showDeleteButton={false}
                      showViewButton={false}
                      onSelect={() => void handleExistingClick(resume)}
                      isRefining={selectingResumeId === resume.id}
                    />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-3">
                    <p className="text-xs text-muted-foreground">
                      {clampedPage * PAGE_SIZE + 1}–
                      {Math.min((clampedPage + 1) * PAGE_SIZE, resumes.length)} of {resumes.length}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border"
                        disabled={clampedPage === 0 || isWorking}
                        onClick={() => setCurrentPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Prev
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border"
                        disabled={clampedPage >= totalPages - 1 || isWorking}
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
